import { useCallback, useEffect, useRef, useState } from 'react';
import { useRefetchOnFocus } from '@redpanda/utils';
import { getProjectById } from '../services/api';
import { useProjectStoreBase, useArchivedProjectStoreBase } from '../stores/pmProjectStore';
import { useDataChanged } from '../utils/dataEvents';
import { PMProject } from '../types/pm';
import { useRequirementStore } from '../stores/pmRequirementStore';
import { usePackageStore } from '../stores/pmPackageStore';
import { useMilestoneStore } from '../stores/pmMilestoneStore';
import { useTaskStore } from '../stores/pmTaskStore';
import { usePaymentStore } from '../stores/pmPaymentStore';

/**
 * GET /projects/:id 已帶回所有子資料：直接填入各 store（換掉這個專案的那幾筆，保留其他專案的）
 * 詳情頁原本另外打 5 支 API 抓同樣的資料；PM 資料庫連線只有 2 條，6 支同時送會排隊（整頁 1.5～2.2 秒）
 */
const CHILD_STORES = [
  [useRequirementStore, 'requirements'],
  [usePackageStore, 'packages'],
  [useMilestoneStore, 'milestones'],
  [useTaskStore, 'tasks'],
  [usePaymentStore, 'payments'],
] as const;

const splitProject = (data: any): PMProject => {
  const { requirements, packages, milestones, tasks, payments, ...project } = data;
  const children = { requirements, packages, milestones, tasks, payments } as Record<string, any[] | undefined>;
  CHILD_STORES.forEach(([store, key]) => {
    const items = children[key];
    if (Array.isArray(items)) store.getState().setProjectItems(project.id, items);
  });
  return project as PMProject;
};

const findCached = (id?: string): PMProject | undefined =>
  id
    ? useProjectStoreBase.getState().items.find(p => p.id === id) ??
      useArchivedProjectStoreBase.getState().items.find(p => p.id === id)
    : undefined;

/**
 * 專案詳情、看板、甘特圖共用：取得單一專案
 * - 列表 store 有這個專案就先顯示（不閃骨架），同時背景向後端重抓（列表只有第一頁、也可能是舊資料）
 * - 換專案（同一頁只換網址 :id）時先清掉上一個專案；上一個專案晚到的回應不覆蓋
 * - 專案、收款、分包異動後重抓（已收／待收款、總預算由後端重算；子資料一起更新）
 */
export const useProject = (id?: string) => {
  const [state, setState] = useState(() => {
    const cached = findCached(id);
    return { id, project: cached ?? null, loading: !cached, notFound: false };
  });

  // render 中重設：換專案時不先畫一格上一個專案的內容
  if (state.id !== id) {
    const cached = findCached(id);
    setState({ id, project: cached ?? null, loading: !cached, notFound: false });
  }

  const currentId = useRef(id);
  currentId.current = id;

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const response = await getProjectById(id);
      if (currentId.current !== id) return;
      // 子資料放進各 store，專案本身不帶子資料（列表 store 會 persist，避免快取變肥）
      const project = splitProject(response.data);
      setState({ id, project, loading: false, notFound: false });
      useProjectStoreBase.getState().updateItem(id, project);
    } catch (error) {
      if (currentId.current !== id) return;
      console.error('Failed to load project:', error);
      setState(prev => ({ ...prev, loading: false, notFound: !prev.project }));
    }
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  useDataChanged(['projects', 'payments', 'packages'], reload);
  useRefetchOnFocus(reload);

  /** 樂觀更新（例：改狀態）：同時更新列表 store */
  const setProject = useCallback((project: PMProject) => {
    setState(prev => ({ ...prev, project }));
    useProjectStoreBase.getState().updateItem(project.id, project);
  }, []);

  return { project: state.project, loading: state.loading, notFound: state.notFound, setProject };
};
